import { Component, forwardRef, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";

import { TranslateModule } from "@ngx-translate/core";
import { MatOption } from "@angular/material/autocomplete";
import { MatChipRemove, MatChipRow } from "@angular/material/chips";
import { MatFormField } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { MatSelect } from "@angular/material/select";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { debounceTime, distinctUntilChanged, filter, Subject, takeUntil, tap } from "rxjs";
import { MatDivider } from "@angular/material/divider";
import { KudosRecipient, KudosRecipients } from "@teammood/timeline";
import { UserActivityService } from "../../../../user-activity/user-activity.service";

@Component({
    selector: 'tm-kudos-recipients-input-list',
    templateUrl: './kudos-recipients-input-list.component.html',
    styleUrls: ['./kudos-recipients-input-list.component.scss'],
    imports: [
    TranslateModule,
    MatChipRemove,
    MatChipRow,
    MatFormField,
    MatIcon,
    MatOption,
    ReactiveFormsModule,
    MatSelect,
    NgxMatSelectSearchModule,
    MatDivider
],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KudosRecipientsInputListComponent),
            multi: true
        },
    ]
})
export class KudosRecipientsInputListComponent implements OnInit, OnDestroy, ControlValueAccessor {

    private teammatesList: KudosRecipient[]
    protected ALL_THE_TEAM: KudosRecipient

    @Input()
    recipients: KudosRecipients // might be set already

    protected filteredRecipientList: KudosRecipient[]

    protected selectedRecipients: KudosRecipient[] = []

    protected allTeamMates: KudosRecipient[]

    protected recipientsCtrl: FormControl<KudosRecipient> = new FormControl({value: null, disabled: true})

    protected recipientFilterCtrl = new FormControl()

    private _destroy$ = new Subject<void>();

    constructor(
        private userActivityService: UserActivityService,
    ) {}

    ngOnInit() {
        this.ALL_THE_TEAM = {id:'all', fullname: 'kudos.card.description.all'}

        this.recipientsCtrl.valueChanges.pipe(
            filter(recipient => recipient !== null),
            tap(recipient => {
                if (recipient.id === this.ALL_THE_TEAM.id){
                    this.recipientsCtrl.disable({emitEvent: false});
                    this.selectedRecipients = [];
                }
                if (this.selectedRecipients.indexOf(recipient) === -1) {
                    this.selectedRecipients.push(recipient);
                    this.teammatesList = this.teammatesList.filter(tm => tm.id !== recipient.id);
                    this.filteredRecipientList = this.teammatesList;
                }
                this.recipientsCtrl.reset(null, {emitEvent: false});
                this.sendKudosRecipientsToParent();
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.recipientFilterCtrl.valueChanges.pipe(
            debounceTime(250),
            distinctUntilChanged(),
            tap(search => {
                if (search === '') {
                    this.filteredRecipientList = this.teammatesList;
                } else {
                    this.filteredRecipientList =  this.teammatesList.filter(tm => tm.fullname.toLowerCase().indexOf(search.toLowerCase()) > -1);
                }
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    removeRecipient(recipientToRemove: KudosRecipient) {
        this.selectedRecipients = this.selectedRecipients.filter(recipient => recipient.id !== recipientToRemove.id);
        this.teammatesList.push(recipientToRemove);
        this.teammatesList.sort((a, b) => a.fullname.localeCompare(b.fullname));
        this.filteredRecipientList = this.teammatesList;
        if (recipientToRemove.id === this.ALL_THE_TEAM.id) {
            this.teammatesList = this.allTeamMates;
            this.filteredRecipientList = this.teammatesList;
            this.recipientsCtrl.enable({emitEvent: true});
        }
        this.sendKudosRecipientsToParent();
    }

    private sendKudosRecipientsToParent() {
      const allTeamSelected = this.selectedRecipients.indexOf(this.ALL_THE_TEAM) !== -1;

        if (allTeamSelected) {
            const recipients = { // onChange: to inform parent for a change.
                allTeamSelected: true,
                selectedTeammates: []
            };
            this.onChange(recipients);
        } else if (this.selectedRecipients.length > 0) {
            const recipients = { // onChange: to inform parent for a change.
                allTeamSelected: false,
                selectedTeammates: this.selectedRecipients
            };
            this.onChange(recipients);
        } else {
            const recipients = { // onChange: to inform parent for a change.
                allTeamSelected: false,
                selectedTeammates: []
            };
            this.onChange(recipients);
        }
    }

    //bind to ngModel
    writeValue(teamId: string): void {
        if (teamId && teamId !== '') {
            this.userActivityService.getTeamMatesList(teamId).then(teamMates => {
                this.recipientsCtrl.enable();
                this.allTeamMates = teamMates
                this.teammatesList = this.allTeamMates;
                this.selectedRecipients = [];

                if (this.recipients?.selectedTeammates.length > 0) {
                    this.selectedRecipients = this.recipients.selectedTeammates;
                    const selectedRecipientsIds = this.recipients.selectedTeammates.map(recipient => recipient.id)
                    this.teammatesList = this.teammatesList.filter(tm => !selectedRecipientsIds.includes(tm.id));
                    this.filteredRecipientList = this.teammatesList.filter(tm => !selectedRecipientsIds.includes(tm.id));
                } else if(this.recipients?.allTeamSelected) {
                    this.recipientsCtrl.setValue(this.ALL_THE_TEAM, {emitEvent: true});
                    this.teammatesList = [];
                }

                this.filteredRecipientList = this.teammatesList;
                this.sendKudosRecipientsToParent();
            })
        } else {
            this.recipientsCtrl.disable();
        }
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    onChange: any = () => {};
    onTouched: any = () => { };
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
}